import AppAvatar from '@app/components/ui/Avatar';
import { getInitials } from '@app/utils/extractFullnameInitials';
import { Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { BsIncognito } from 'react-icons/bs';
import { VisitRejectedNotificationType } from '../../types/notifications';
import { notificationSentAtFormattedDate } from '../../utils';

type RejectedVisitNotificationCardProps = {
  notificationDetails: VisitRejectedNotificationType;
  onClick: () => void;
};

const RejectedVisitNotificationCard = ({ notificationDetails, onClick }: RejectedVisitNotificationCardProps) => {
  const { t } = useTranslation();
  const meta = notificationDetails.metadata?.[0];

  const fullname = meta?.caregiverNames?.[0] || '';
  const caregiverFullnameLetters = getInitials(fullname, { fallback: '' });

  const clientFullName =
    (meta?.clientFirstName ? meta.clientFirstName : '') + (meta?.clientLastName ? ` ${meta.clientLastName}` : '');

  return (
    <div
      onClick={onClick}
      className={
        'flex items-start gap-4 bg-white shadow-sm shadow-red-200 rounded-[10px] p-4 border border-red-200 hover:shadow-md transition-shadow'
      }
    >
      {meta?.caregiverNames?.[0] ? (
        <div className="h-full flex items-center justify-center">
          <AppAvatar isSelected={true} fullName={fullname} value={caregiverFullnameLetters} size={'large'} />
        </div>
      ) : (
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 text-sm font-semibold text-slate-700">
          <BsIncognito fontSize={20} />
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between">
          <div className="min-w-0 mb-1">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">{t('notifications-visit-label')}</h3>

              <Tag color="red">{t('notifications-tag-rejected')}</Tag>
            </div>

            {notificationDetails.message && (
              <p className="mt-1 text-sm text-gray-700 line-clamp-3">
                {t('notifications-card-visit-rejected-body', { name: fullname, address: meta?.address })}
              </p>
            )}
          </div>

          {/* Right side: read indicator + timestamps */}
          <div className="ml-4 flex shrink-0 flex-col items-end gap-2">
            <span
              className={`inline-block h-2.5 w-2.5 rounded-full ${notificationDetails.isRead ? 'bg-gray-300' : 'bg-indigo-600'}`}
            />

            <div className="text-right text-xs text-gray-500">
              {t('notifications-sent-at', { time: notificationSentAtFormattedDate(notificationDetails.sentAt) })}
            </div>
          </div>
        </div>

        {clientFullName && (
          <div className="flex gap-1">
            <span className="font-small text-gray-500">{t('notifications-patient', { name: clientFullName })}</span>
          </div>
        )}

        {meta?.reason && (
          <p className="mt-1 text-sm italic text-gray-600 line-clamp-2">
            {t('notifications-card-visit-rejected-reason', { reason: meta.reason })}
          </p>
        )}
      </div>
    </div>
  );
};

export default RejectedVisitNotificationCard;
